import PublicContentPage from '@/components/PublicContentPage/PublicContentPage';
import { CONSTANTS } from '@/constants';
import { getEkardoApolloClient } from '@netfront/common-library';
import {
  GET_CONTENT_PAGE_BY_URL_QUERY,
  IContentPage,
  IGetContentPageByUrlQueryGraphQLResponse,
  IGetContentPageByUrlQueryVariables,
  ISearchContentGroupsQueryGraphQLResponse,
  ISearchContentGroupsQueryVariables,
  SEARCH_CONTENT_GROUPS_QUERY,
} from '@netfront/ekardo-content-library';
import { GetStaticPathsResult, NextPage, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

export interface IStaticPropsContextParams extends ParsedUrlQuery {
  pageUrl: string;
}

const ContentNextPage: NextPage<{ contentPage: IContentPage }> = ({ contentPage }: { contentPage: IContentPage }) => {
  const {
    query: { pageUrl },
  } = useRouter();

  return <PublicContentPage contentPage={contentPage} pageUrl={String(pageUrl)} />;
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = getEkardoApolloClient({
    product: 'EKARDO',
  });

  const {
    data: {
      contentGroup: { searchContentGroups },
    },
  } = await client.query<ISearchContentGroupsQueryGraphQLResponse, ISearchContentGroupsQueryVariables>({
    query: SEARCH_CONTENT_GROUPS_QUERY,
    variables: {
      projectId: String(process.env.REACT_APP_PROJECT_ID),
      type: 'PAGE',
    },
  });

  const contentPages = searchContentGroups.map(({ contentPages: groupContentPages }) => groupContentPages).flat();

  const paths = contentPages
    .filter(({ url }) => !url.includes('deleted'))
    .map(({ url: pageUrl }) => ({
      params: {
        pageUrl,
      },
    }));

  return {
    paths,
    fallback: false,
  };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { pageUrl } = params as IStaticPropsContextParams;

  const client = getEkardoApolloClient({
    product: 'EKARDO',
  });

  try {
    const {
      data: {
        contentPage: { getContentPageByUrl: contentPage },
      },
    } = await client.query<IGetContentPageByUrlQueryGraphQLResponse, IGetContentPageByUrlQueryVariables>({
      query: GET_CONTENT_PAGE_BY_URL_QUERY,
      variables: {
        contentPageUrl: String(pageUrl),
        projectId: String(process.env.REACT_APP_PROJECT_ID),
      },
    });

    if (!contentPage) {
      return { notFound: true };
    }

    return {
      props: {
        contentPage,
      },
      revalidate: CONSTANTS.STATIC_PAGE_REGENERATION_TIME_IN_SECONDS,
    };
  } catch {
    return { notFound: true };
  }
};

// eslint-disable-next-line import/no-default-export
export default ContentNextPage;
